<template>
    <BaseCard padding="md" class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div class="flex-1 min-w-0 flex flex-col gap-1">
            <div class="flex items-center gap-2 flex-wrap">
                <span class="body-semibold text-ink truncate">{{ campaign.name }}</span>
                <StatusChip state="pending">{{ t("reflectionsAdmin.campaign.state.draft") }}</StatusChip>
            </div>
            <div class="body-small text-ink-muted">{{ audienceLine }}</div>
            <div class="details text-ink-muted">{{ dateLine }}</div>
        </div>

        <div class="flex items-center gap-2 md:shrink-0">
            <BaseButton
                theme="neutral"
                size="sm"
                icon-only
                :aria-label="t('reflections.shared.actionLabel', { action: t('reflections.admin.drafts.deleteDraft'), target: campaign.name })"
                :title="t('reflections.admin.drafts.deleteDraft')"
                :disabled="busy"
                @click="emit('delete', campaign)">
                <i class="bi bi-trash" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton
                theme="neutral"
                size="sm"
                :aria-label="t('reflections.shared.actionLabel', { action: t('reflections.admin.drafts.launch'), target: campaign.name })"
                :disabled="busy"
                @click="emit('launch', campaign)">
                <i class="bi bi-send" aria-hidden="true"></i>
                {{ t("reflections.admin.drafts.launch") }}
            </BaseButton>
            <BaseButton
                type="button"
                theme="primary"
                size="sm"
                :aria-label="t('reflections.shared.actionLabel', { action: t('common.edit'), target: campaign.name })"
                :disabled="busy"
                @click="emit('edit', campaign)"
                >{{ t("common.edit") }}</BaseButton
            >
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    campaign: AdminCampaignSummary;
    busy: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{
    (event: "delete", campaign: AdminCampaignSummary): void;
    (event: "launch", campaign: AdminCampaignSummary): void;
    (event: "edit", campaign: AdminCampaignSummary): void;
}>();

// Campaigns always invite the whole eligible org, so the audience is the eligible count.
const audienceLine = computed(() => {
    const values = { template: props.campaign.template.name, count: props.campaign.eligible_participant_count };
    return props.campaign.is_self_assessment_only ? t("reflections.admin.drafts.audienceSelfOnly", values) : t("reflections.admin.drafts.audience", values);
});

// The campaign records who created it but not who last edited it, so only
// the creator is attributed.
const dateLine = computed(() => {
    const created = formatDate(props.campaign.created_at, "short");
    const createdLine = props.campaign.created_by_name
        ? t("reflections.admin.drafts.createdBy", { date: created, name: props.campaign.created_by_name })
        : t("reflections.admin.drafts.created", { date: created });
    const edited = formatDate(props.campaign.updated_at, "short");
    const parts = [created ? createdLine : "", edited ? t("reflections.admin.drafts.lastEdited", { date: edited }) : ""].filter(Boolean);
    return parts.join(" · ");
});
</script>
